import React from 'react';

/**
 * BEELYV icon family — drawn from the approved brand board.
 *
 * Every glyph shares one grammar: 24×24 grid, 2px optical padding, round caps
 * and joins, no fills, organic curves over hard angles, one stroke weight
 * (set by <Icon />). Props mirror lucide-react so glyphs drop into any slot
 * that already accepts a lucide icon.
 */
export interface GlyphProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
  strokeWidth?: number | string;
}
function glyph(name: string, body: React.ReactNode) {
  function Glyph({
    size = 20,
    strokeWidth = 1.6,
    className = '',
    ...rest
  }: GlyphProps) {
    return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className={`bee-glyph ${className}`} {...rest} data-id="element-821" data-mp="1gvobld3tm763">
        {body}
      </svg>;
  }
  Glyph.displayName = `BeeGlyph(${name})`;
  return Glyph;
}

/* ---------- Core wellness family (brand board, row 1) ---------- */

export const WorkoutGlyph = glyph('workout', <>
    <rect x="5.5" y="7" width="3" height="10" rx="1.3" data-id="element-822" data-mp="244bcsadbq9xx" />
    <rect x="15.5" y="7" width="3" height="10" rx="1.3" data-id="element-823" data-mp="294zbzjbphx38" />
    <path d="M8.5 12h7M3.5 10v4M20.5 10v4" data-id="element-824" data-mp="292ctwpr6vz6r" />
  </>);
export const NutritionGlyph = glyph('nutrition', <>
    <path d="M12 8.2c-1.7-1.3-4.7-1.2-6 1-1.6 2.6-.5 6.8 1.6 9.2 1.3 1.5 2.8 1.8 4.4.9 1.6.9 3.1.6 4.4-.9 2.1-2.4 3.2-6.6 1.6-9.2-1.3-2.2-4.3-2.3-6-1Z" data-id="element-825" data-mp="2i5e0erjyvsay" />
    <path d="M12 8.2c0-2 .7-3.4 2.2-4.2" data-id="element-826" data-mp="2mxfb491hlayv" />
    <path d="M12.3 6c1.3-.4 2.6 0 3.3 1" data-id="element-827" data-mp="2rv7afo4gnzd3" />
  </>);
export const MindGlyph = glyph('mind', <>
    <path d="M12 5c-2 2.3-2 6.9 0 10 2-3.1 2-7.7 0-10Z" data-id="element-828" data-mp="2wua64hyjwt8e" />
    <path d="M12 15c-2.6-.3-5.1-2.5-5.7-6.1 2.3.2 4.3 1.3 5.7 3.1" data-id="element-829" data-mp="2wvv0o7kv8c69" />
    <path d="M12 15c2.6-.3 5.1-2.5 5.7-6.1-2.3.2-4.3 1.3-5.7 3.1" data-id="element-830" data-mp="31slfkrkxp1q3" />
    <path d="M6 18.5h12" data-id="element-831" data-mp="2c58xu5js10ib" />
  </>);

/* Crescent shifted +0.8 so its visual mass sits on the optical centre of the tile. */
export const SleepGlyph = glyph('sleep', <path d="M19.8 14.6A7.6 7.6 0 0 1 10.2 5a7.6 7.6 0 1 0 9.6 9.6Z" data-id="element-832" data-mp="277pljb6n2xt0" />);
export const WaterGlyph = glyph('water', <>
    <path d="M12 3.6c3.3 4 5.6 7.2 5.6 10.1a5.6 5.6 0 0 1-11.2 0C6.4 10.8 8.7 7.6 12 3.6Z" data-id="element-833" data-mp="2lwvhmqa6k7wo" />
  </>);
export const CommunityGlyph = glyph('community', <>
    <circle cx="12" cy="7.6" r="2.4" data-id="element-834" data-mp="2uufkg3noyc9e" />
    <circle cx="6.3" cy="9.6" r="1.8" data-id="element-835" data-mp="2uxjwfn7eecam" />
    <circle cx="17.7" cy="9.6" r="1.8" data-id="element-836" data-mp="2pxfdlqj0p5h8" />
    <path d="M7.8 19.2c0-2.6 1.9-4.7 4.2-4.7s4.2 2.1 4.2 4.7" data-id="element-837" data-mp="2pwe2jm204izi" />
    <path d="M3.4 18c0-2 1.3-3.7 3-4" data-id="element-838" data-mp="34u6czzsoyh6u" />
    <path d="M20.6 18c0-2-1.3-3.7-3-4" data-id="element-839" data-mp="34t51ju7rjsol" />
  </>);

/* ---------- Sports family (brand board, row 2) ---------- */

export const RunningGlyph = glyph('running', <>
    <circle cx="14.8" cy="4.8" r="1.7" data-id="element-840" data-mp="1agx1k2gl4lly" />
    <path d="M13.2 8.6 10.7 14l3 2.6-1.1 4.4" data-id="element-841" data-mp="1ahzv6i5al25k" />
    <path d="M10.7 14 7.2 19.8" data-id="element-842" data-mp="15hkdxfhw3aib" />
    <path d="M13.2 8.6 9.8 9.7l-2 2.7" data-id="element-843" data-mp="15in75u2opp1e" />
    <path d="M13.2 8.6l1.9 3 3.3.7" data-id="element-844" data-mp="3h4e9bbgoyc84" />
  </>);
export const CyclingGlyph = glyph('cycling', <>
    <circle cx="6" cy="16" r="3.6" data-id="element-845" data-mp="3m2z8r8o4crvi" />
    <circle cx="18" cy="16" r="3.6" data-id="element-846" data-mp="3m1xms95kekz0" />
    <path d="M6 16 9.8 9.4h4.8L18 16" data-id="element-847" data-mp="3m51ydrld0izp" />
    <path d="M9.8 9.4 13 16H6" data-id="element-848" data-mp="37f4ehnugqfgb" />
    <path d="M13.2 6.8h2.4" data-id="element-849" data-mp="37dt9jkpa71nm" />
  </>);
export const SwimmingGlyph = glyph('swimming', <>
    <circle cx="17.2" cy="8.2" r="1.7" data-id="element-850" data-mp="3cbuts8yg170o" />
    <path d="M5 12.6 9.4 9.2l3.4 2.6 2.4-1.4" data-id="element-851" data-mp="2ydo6fmr4epf9" />
    <path d="M3 16.4c1.5 0 1.5-1.1 3-1.1s1.5 1.1 3 1.1 1.5-1.1 3-1.1 1.5 1.1 3 1.1 1.5-1.1 3-1.1 1.5 1.1 3 1.1" data-id="element-852" data-mp="2ycv7v92bf687" />
    <path d="M3 20c1.5 0 1.5-1.1 3-1.1S7.5 20 9 20s1.5-1.1 3-1.1 1.5 1.1 3 1.1 1.5-1.1 3-1.1 1.5 1.1 3 1.1" data-id="element-853" data-mp="32gt2p7klxuvg" />
  </>);
export const CrossfitGlyph = glyph('crossfit', <>
    <path d="M9 10.2V7.9a3 3 0 0 1 6 0v2.3" data-id="element-854" data-mp="2tfmkmu2b8c03" />
    <path d="M8.2 10.2h7.6a6 6 0 1 1-7.6 0Z" data-id="element-855" data-mp="2tgejcdr7epul" />
  </>);
export const TrophyGlyph = glyph('trophy', <>
    <path d="M8 4h8v5a4 4 0 0 1-8 0V4Z" data-id="element-856" data-mp="mmamvp28aplc" />
    <path d="M8 6H5.6a2.6 2.6 0 0 0 2.7 3.3" data-id="element-857" data-mp="rm5gi0de1ta3" />
    <path d="M16 6h2.4a2.6 2.6 0 0 1-2.7 3.3" data-id="element-858" data-mp="rkudw3voirkk" />
    <path d="M12 13v6.5" data-id="element-859" data-mp="2nql875uqiz6h" />
    <path d="M8.8 19.5h6.4" data-id="element-860" data-mp="2nptg450cjjky" />
  </>);
export const HeartGlyph = glyph('heart', <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" data-id="element-861" data-mp="2isbc8t16rujt" />);

/* ---------- Navigation family ---------- */

export const HomeGlyph = glyph('home', <path d="M4 10.6 12 4l8 6.6V19a1.5 1.5 0 0 1-1.5 1.5H15v-5.2a3 3 0 0 0-6 0v5.2H5.5A1.5 1.5 0 0 1 4 19v-8.4Z" data-id="element-862" data-mp="29r3imo9m7hw2" />);
export const CheckGlyph = glyph('check', <>
    <circle cx="12" cy="12" r="8.2" data-id="element-863" data-mp="24tbr3vcvtxse" />
    <path d="m8.6 12.3 2.4 2.4 4.9-5.1" data-id="element-864" data-mp="200il745ogjeq" />
  </>);
export const ProgressGlyph = glyph('progress', <>
    <path d="M4 16.5c2.8 0 3.8-4.6 6.8-4.6s3.4 2.8 5.8 1.4S19.4 8 20 6" data-id="element-865" data-mp="1v0w58mygsp28" />
    <path d="M4 20h16" data-id="element-866" data-mp="1r0catr59zzjs" />
  </>);
export const ProfileGlyph = glyph('profile', <>
    <circle cx="12" cy="8.4" r="3.6" data-id="element-867" data-mp="1m0zpxbxyt2ju" />
    <path d="M5 20c.8-3.6 3.6-5.8 7-5.8s6.2 2.2 7 5.8" data-id="element-868" data-mp="1lzy1m5rxujk6" />
  </>);

/* ---------- Hive / gamification family ---------- */

export const HiveGlyph = glyph('hive', <>
    <path d="m12 3.5 7.4 4.25v8.5L12 20.5l-7.4-4.25v-8.5L12 3.5Z" data-id="element-869" data-mp="3spi76kzfv7lm" />
    <path d="m12 8.6 3 1.7v3.4l-3 1.7-3-1.7v-3.4l3-1.7Z" data-id="element-870" data-mp="3srchl5d33hbg" />
  </>);

/** Bee reward: a single honeycomb cell holding a nectar drop. */
export const BeeRewardGlyph = glyph('beeReward', <>
    <path d="m12 3.5 7.4 4.25v8.5L12 20.5l-7.4-4.25v-8.5L12 3.5Z" data-id="element-871" data-mp="6itd4gjnyqun" />
    <path d="M12 8.4c1.5 1.8 2.5 3.2 2.5 4.5a2.5 2.5 0 0 1-5 0c0-1.3 1-2.7 2.5-4.5Z" data-id="element-872" data-mp="ailpl4c6hh5a" />
  </>);

/** Hive Chest: a rounded chest with a honeycomb clasp. */
export const HiveChestGlyph = glyph('hiveChest', <>
    <path d="M4 10.5h16V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7.5Z" data-id="element-873" data-mp="3eymwd35dqgfe" />
    <path d="M4 10.5C4 7 6.5 5 12 5s8 2 8 5.5" data-id="element-874" data-mp="3iyx0095p8ur3" />
    <path d="m12 11.6 1.9 1.1v2.2L12 16l-1.9-1.1v-2.2l1.9-1.1Z" data-id="element-875" data-mp="3iy5exs9qaff2" />
  </>);

/** Leaf — nature accent from the graphic-elements row. */
export const LeafGlyph = glyph('leaf', <>
    <path d="M5 19C5 10.2 10.2 5 19 5c0 8.8-5.2 14-14 14Z" data-id="element-876" data-mp="2r7hekifvpe2g" />
    <path d="m5 19 8.2-8.2" data-id="element-877" data-mp="2v9m8zje5ieq5" />
  </>);

/* ---------- Social + progress ---------- */

/** Streak: an organic flame, drawn as one continuous outline with an inner tongue. */
export const StreakGlyph = glyph('streak', <>
    <path d="M12 20.5c-3.6 0-6-2.5-6-5.8 0-2.9 1.9-4.6 3.2-6.4.9 1.2 1.6 1.9 2.4 2.2-.2-2.6.8-5 2.7-7 .4 2.9 1.6 4.6 2.8 6.3 1 1.4 1.9 2.8 1.9 4.9 0 3.3-2.4 5.8-7 5.8Z" data-id="element-878" data-mp="3tknepevmch48" />
  </>);

/** Comment: a soft speech bubble with a small organic tail. */
export const CommentGlyph = glyph('comment', <path d="M12 4.5c4.6 0 8 3 8 6.8s-3.4 6.8-8 6.8c-1 0-2-.1-2.9-.4L5 19.5l1-3.6c-1.3-1.2-2-2.8-2-4.6 0-3.8 3.4-6.8 8-6.8Z" data-id="element-879" data-mp="3fl47coamt9sz" />);

/** Follow: profile figure with a plus. */
export const FollowGlyph = glyph('follow', <>
    <circle cx="10" cy="8.4" r="3.4" data-id="element-880" data-mp="3ondfx8mcat3q" />
    <path d="M3.6 20c.8-3.4 3.3-5.5 6.4-5.5 1.5 0 2.8.5 3.9 1.3" data-id="element-881" data-mp="3ok971xxxj96q" />
    <path d="M18 13.5v6M15 16.5h6" data-id="element-882" data-mp="3kjfid2cnxqcs" />
  </>);

/** Following: profile figure with a check. */
export const FollowingGlyph = glyph('following', <>
    <circle cx="10" cy="8.4" r="3.4" data-id="element-883" data-mp="zv1h1gv7iqoe" />
    <path d="M3.6 20c.8-3.4 3.3-5.5 6.4-5.5 1.5 0 2.8.5 3.9 1.3" data-id="element-884" data-mp="ztzvujkh8nsy" />
    <path d="m15 17 2 2 4-4.2" data-id="element-885" data-mp="17drau01jcd2l" />
  </>);

/** Biomarkers: a laboratory vial with a fill line and a drop. */
export const BiomarkerGlyph = glyph('biomarker', <>
    <path d="M8.5 3.5h7" data-id="element-886" data-mp="1cbaph125arv2" />
    <path d="M9.6 3.5v12.3a2.4 2.4 0 0 0 4.8 0V3.5" data-id="element-887" data-mp="xgctly7sfk98" />
    <path d="M9.6 11.5h4.8" data-id="element-888" data-mp="xhxoxq1xf785" />
    <path d="M18.5 12.6c.9 1.1 1.5 2 1.5 2.8a1.5 1.5 0 0 1-3 0c0-.8.6-1.7 1.5-2.8Z" data-id="element-889" data-mp="12f7mjszgyp72" />
  </>);

/* ---------- Settings categories ---------- */

export const LanguageGlyph = glyph('language', <>
    <circle cx="12" cy="12" r="8.2" data-id="element-890" data-mp="1v3vbpznc9ypw" />
    <path d="M3.8 12h16.4" data-id="element-891" data-mp="1v2tnsul85hqr" />
    <path d="M12 3.8c2.2 2.3 3.3 5 3.3 8.2s-1.1 5.9-3.3 8.2c-2.2-2.3-3.3-5-3.3-8.2S9.8 6.1 12 3.8Z" data-id="element-892" data-mp="1v33fcmlx3wyk" />
  </>);
export const UnitsGlyph = glyph('units', <>
    <rect x="3.5" y="8" width="17" height="8" rx="2.4" data-id="element-893" data-mp="1lda63s98ztxh" />
    <path d="M7.5 8v3M11 8v4.5M14.5 8v3M18 8v2" data-id="element-894" data-mp="1lbpadzb764y1" />
  </>);
export const NotificationGlyph = glyph('notification', <>
    <path d="M6.2 16.5V11a5.8 5.8 0 0 1 11.6 0v5.5l1.4 1.6H4.8l1.4-1.6Z" data-id="element-895" data-mp="2du30fcqakmmk" />
    <path d="M10.2 20.2a2 2 0 0 0 3.6 0" data-id="element-896" data-mp="2dw7cwj29atyg" />
  </>);
export const AccessibilityGlyph = glyph('accessibility', <>
    <circle cx="12" cy="5.6" r="1.8" data-id="element-897" data-mp="91eltohfde5y" />
    <path d="M5.5 9.2c2.2.7 4.3 1 6.5 1s4.3-.3 6.5-1" data-id="element-898" data-mp="90bw3jvu9hrm" />
    <path d="M12 10.2v4.3M12 14.5l-2.6 5.5M12 14.5l2.6 5.5" data-id="element-899" data-mp="4zt5ft5omhr0" />
  </>);

/** Connection — the overlapping circles from the brand board's "Conexão" element. */
export const ConnectionGlyph = glyph('connection', <>
    <circle cx="9" cy="12" r="5" data-id="element-900" data-mp="wrhf27x2sq65" />
    <circle cx="15" cy="12" r="5" data-id="element-901" data-mp="wpo2kbzs7bq8" />
  </>);
export const PrivacyGlyph = glyph('privacy', <>
    <path d="M12 3.6 18.8 6v5.4c0 4.3-2.8 7.6-6.8 9-4-1.4-6.8-4.7-6.8-9V6L12 3.6Z" data-id="element-902" data-mp="sqp336wtk766" />
    <circle cx="12" cy="11" r="1.7" data-id="element-903" data-mp="np84xj5pyyta" />
    <path d="M12 12.7v2.6" data-id="element-904" data-mp="nsdv71q346ql" />
  </>);
export const SecurityGlyph = glyph('security', <>
    <path d="M12 3.6 18.8 6v5.4c0 4.3-2.8 7.6-6.8 9-4-1.4-6.8-4.7-6.8-9V6L12 3.6Z" data-id="element-905" data-mp="iqyaxbvqe499" />
    <path d="m9.2 12 2 2 3.7-3.9" data-id="element-906" data-mp="38i38vbf1xmhp" />
  </>);
export const HelpGlyph = glyph('help', <>
    <circle cx="12" cy="12" r="8.2" data-id="element-907" data-mp="33gnnfi8ypdys" />
    <path d="M9.7 9.6a2.4 2.4 0 0 1 4.6.9c0 1.6-2.3 2-2.3 3.4" data-id="element-908" data-mp="33jrwou1aazwb" />
    <path d="M12 16.8h.01" data-id="element-909" data-mp="2tf1s0gehxhoe" />
  </>);

/* ---------- Connected-service categories (never third-party logos) ---------- */

/** Health platform: heart with a sync arc. */
export const HealthSyncGlyph = glyph('healthSync', <>
    <path d="M12 17.5s-5.5-3.4-5.5-7.8A3.1 3.1 0 0 1 12 7.7a3.1 3.1 0 0 1 5.5 2c0 4.4-5.5 7.8-5.5 7.8Z" data-id="element-910" data-mp="339lf1yzfhbb5" />
    <path d="M4 12a8 8 0 0 1 1.4-4.5M20 12a8 8 0 0 1-1.4 4.5" data-id="element-911" data-mp="3847r84dc37wa" />
  </>);

/** Activity platform: a route between two points. */
export const ActivitySyncGlyph = glyph('activitySync', <>
    <circle cx="6" cy="18" r="1.8" data-id="element-912" data-mp="3h55u2jc1d9da" />
    <circle cx="18" cy="6" r="1.8" data-id="element-913" data-mp="3m1mkjk6pjztj" />
    <path d="M7.6 17.2c3.4-.4 2.8-3.6 5.2-4.4 2.3-.8 3.6.2 4.5-2.4.3-.9.4-1.8.4-2.6" data-id="element-914" data-mp="3m2p9hmkgzs6t" />
  </>);

/** Wearable: a rounded band device. */
export const WearableGlyph = glyph('wearable', <>
    <rect x="6.5" y="7" width="11" height="10" rx="3" data-id="element-915" data-mp="1rpg5gaap0yvy" />
    <path d="M9 7 9.6 3.5h4.8L15 7M9 17l.6 3.5h4.8L15 17" data-id="element-916" data-mp="1wmy5tcana5sc" />
    <path d="M12 10v2.2l1.4 1" data-id="element-917" data-mp="1wnhqv1vldaal" />
  </>);

/* ---------- Mood family (replaces emoji glyphs) ---------- */

export const MoodDrainedGlyph = glyph('moodDrained', <>
    <rect x="3.5" y="8" width="15" height="8" rx="2.4" data-id="element-918" data-mp="3uzot80g01ph" />
    <path d="M20.5 11v2" data-id="element-919" data-mp="7vjke75dax9i" />
    <path d="M6.5 11v2" data-id="element-920" data-mp="7v1ojbmmal24" />
  </>);
export const MoodTiredGlyph = glyph('moodTired', <>
    <circle cx="12" cy="12" r="8.2" data-id="element-921" data-mp="cv5x8fhaabhs" />
    <path d="M7.8 10.2c.8.8 2.2.8 3 0M13.2 10.2c.8.8 2.2.8 3 0" data-id="element-922" data-mp="cw7lxmr82whz" />
    <path d="M10.4 15.4h3.2" data-id="element-923" data-mp="hu95e8sk8xtz" />
  </>);
export const MoodNeutralGlyph = glyph('moodNeutral', <>
    <circle cx="12" cy="12" r="8.2" data-id="element-924" data-mp="msap8vxt916i" />
    <path d="M9.3 10h.01M14.7 10h.01" data-id="element-925" data-mp="mr97k8mahgft" />
    <path d="M9 15h6" data-id="element-926" data-mp="rjiztvavmsl9" />
  </>);
export const MoodGoodGlyph = glyph('moodGood', <>
    <circle cx="12" cy="12" r="8.2" data-id="element-927" data-mp="rlwsjxi33spn" />
    <path d="M9.3 10h.01M14.7 10h.01" data-id="element-928" data-mp="vldx8oposltr" />
    <path d="M8.8 14c.8 1.2 1.9 1.8 3.2 1.8s2.4-.6 3.2-1.8" data-id="element-929" data-mp="vkcncnkeq5dm" />
  </>);
export const MoodFocusedGlyph = glyph('moodFocused', <>
    <circle cx="12" cy="12" r="8.2" data-id="element-930" data-mp="15j24ge4hblst" />
    <circle cx="12" cy="12" r="4.2" data-id="element-931" data-mp="15k3fwjpeqab2" />
    <path d="M12 12h.01" data-id="element-932" data-mp="15h7e2kafv1hc" />
  </>);